import type * as React from "react";
import { ArrowDown, ArrowRight, ArrowUp } from "lucide-react";

import { cn } from "@ando/ui";
import type { AdminKpiCard as AdminKpi } from "@ando/core";

/**
 * AdminKpiCard — reusable KPI tile rendered by every admin v2 surface
 * (overview / cost / health / quality). Mirrors the v1 wire shape from
 * ``admin_v2_models.py::KpiCard``.
 *
 * DRY: ported from the v1 ``HypermemAdminPage`` inline jsx into a shared
 * component because cost/health/quality all need the same big-number +
 * delta % + unit pattern.
 */
export interface AdminKpiCardProps {
  kpi: AdminKpi;
  className?: string;
}

function formatValue(value: number, unit: string): string {
  if (unit === "$") return `$${value.toFixed(2)}`;
  if (unit === "%") return `${value.toFixed(1)}%`;
  if (unit === "ms") return `${Math.round(value)}ms`;
  // Integer-ish big numbers (DAU/WAU/MAU) — drop the fractional part.
  if (Number.isInteger(value) || Math.abs(value) >= 1000) {
    return Math.round(value).toLocaleString("it-IT");
  }
  return value.toFixed(2);
}

export function AdminKpiCard({
  kpi,
  className,
}: AdminKpiCardProps): React.ReactElement {
  const hasDelta = kpi.delta_pct != null;
  const positive = (kpi.delta_pct ?? 0) > 0;
  const neutral = (kpi.delta_pct ?? 0) === 0;
  const DeltaIcon = hasDelta
    ? neutral
      ? ArrowRight
      : positive
        ? ArrowUp
        : ArrowDown
    : null;

  return (
    <div
      className={cn(
        "rounded-lg border border-hairline bg-card/55 p-3.5",
        className,
      )}
    >
      <div className="mb-1.5 text-[10px] text-ink-3 uppercase tracking-wide">
        {kpi.label}
      </div>
      <div className="font-display font-semibold text-[22px] text-ink-1 tabular-nums">
        {formatValue(kpi.value, kpi.unit)}
      </div>
      {hasDelta && DeltaIcon ? (
        <div
          className={cn(
            "mt-0.5 inline-flex items-center gap-1 text-[11px] tabular-nums",
            neutral
              ? "text-ink-3"
              : positive
                ? "text-success"
                : "text-destructive",
          )}
          title={
            kpi.previous_value != null
              ? `Periodo precedente: ${formatValue(kpi.previous_value, kpi.unit)}`
              : undefined
          }
        >
          <DeltaIcon size={10} aria-hidden />
          {(kpi.delta_pct ?? 0).toFixed(1)}% vs precedente
        </div>
      ) : null}
    </div>
  );
}

/** Grid wrapper — keeps the KPI rail layout consistent across pages. */
export function AdminKpiGrid({
  kpis,
}: {
  kpis: readonly AdminKpi[];
}): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {kpis.map((k) => (
        <AdminKpiCard key={k.label} kpi={k} />
      ))}
    </div>
  );
}
